import React, { useState, useMemo } from 'react';

import { LinkOutlined, PlusOutlined } from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import { MultipleValue } from '@shared/table/Rules/component';
import TableIndex from '@shared/table/TableIndex';
import type { TableService, SupportRule } from '@shared/table/TableIndex';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { consumableService } from '@services/admin/consumableService';

interface ConsumableRow {
  id: string;
  name?: string;
  barcode?: string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  volume?: number | string;
  created_at?: string;
  editable?: boolean;
  deletable?: boolean;
  [key: string]: unknown;
}

export default function ConsumablesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastModified, setLastModified] = useState(0);

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: 'models.consumable.name',
      persist: true,
      sortable: true,
      sortFieldName: 'consumables.name',
      width: '200px',
      wrap: true,
      cell: ({ id, name }: ConsumableRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/consumables/${id}`} style={{ wordBreak: 'break-word' }}>
            {name}
          </Link>
          <a
            href={`/admin/consumables/${id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      nameKey: 'models.consumable.barcode',
      sortable: true,
      sortFieldName: 'consumables.barcode',
      wrap: true,
      selector: ({ barcode }: ConsumableRow) => barcode,
    },
    {
      nameKey: 'models.consumable.length',
      width: '100px',
      selector: ({ length }: ConsumableRow) => length,
    },
    {
      nameKey: 'models.consumable.width',
      width: '100px',
      selector: ({ width }: ConsumableRow) => width,
    },
    {
      nameKey: 'models.consumable.height',
      width: '100px',
      selector: ({ height }: ConsumableRow) => height,
    },
    {
      nameKey: 'models.consumable.volume',
      sortable: false,
      width: '120px',
      selector: ({ volume }: ConsumableRow) => volume,
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'consumables.id',
      width: '120px',
      selector: ({ created_at }: ConsumableRow) => created_at,
    },
    {
      nameKey: 'common.table.actions',
      width: '150px',
      cell: ({ id, editable, deletable }: ConsumableRow) => {
        // 權限控管：如果沒有任何權限，顯示 "-"
        if (!editable && !deletable) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* 權限控管：只有當 editable 為 true 時才顯示編輯按鈕 */}
            {editable && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Link to={`/consumables/${id}/edit`}>
                  {t('common.button.edit')}
                </Link>
                <a
                  href={`/admin/consumables/${id}/edit`}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()} rel="noreferrer"
                >
                  <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
                </a>
              </div>
            )}
            {/* 權限控管：只有當 deletable 為 true 時才顯示刪除按鈕 */}
            {deletable && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (window.confirm(t('common.confirm.delete'))) {
                    // 刪除邏輯
                    console.log('Delete consumable:', id);
                  }
                }}
                style={{ color: '#ff4d4f' }}
              >
                {t('common.button.delete')}
              </a>
            )}
          </div>
        );
      },
    },
  ], [t]);

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('models.consumable.name'),
      component: MultipleValue,
      inputName: 'consumables.name',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('admin.consumables.list.filter_tooltips.name.title'),
        content: t('admin.consumables.list.filter_tooltips.name.content'),
      },
    },
    {
      label: t('models.consumable.barcode'),
      component: MultipleValue,
      inputName: 'consumables.barcode',
      createable: true,
      tooltip: {
        title: t('admin.consumables.list.filter_tooltips.barcode.title'),
        content: t('admin.consumables.list.filter_tooltips.barcode.content'),
      },
    },
  ], [t]);

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['data', 'consumables']}
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/consumables/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'prefix',
          searchFields: [
            t('models.consumable.name'),
            t('models.consumable.barcode'),
          ],
        }}
        columnsEditable={true}
        tableId='AdminConsumablesTable'
        defaultQuery={{
          sorting_field: 'consumables.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={consumableService as unknown as TableService}
        filterHelpExample={t('admin.consumables.list.filter_help_example') as string}
        filterDocsUrl="/docs/admin/consumables/list#features"
      />
    </div>
  );
}
